import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo, articleSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { Markdown } from "../components/Markdown";
import { CtaBanner } from "../components/CtaBanner";
import { api } from "../lib/api";

const CATEGORIES = ["All", "Guides", "VPN News", "Security Tips", "Deals"];

export const BlogIndex = () => {
  const [cat, setCat] = useState("All");
  const [allPosts, setAllPosts] = useState(null);

  useEffect(() => {
    api.get("/posts").then((r) => setAllPosts(r.data)).catch(() => setAllPosts([]));
  }, []);

  const posts = allPosts ? (cat === "All" ? allPosts : allPosts.filter((p) => p.category === cat)) : [];
  return (
    <Layout>
      <Seo
        title="VPN Blog: Reviews, Comparisons & Guides | VPNReviews.ai"
        description="Weekly VPN analysis — reviews, head-to-head comparisons, streaming guides, and security explainers from the VPNReviews.ai editorial team."
      />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">The Blog</p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["VPN Analysis,", "Published Weekly"]} />
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
            Reviews, comparisons, streaming guides, and security explainers — 1-2 new articles every week.
          </p>
        </div>
      </section>
      <Breadcrumbs crumbs={[{ label: "Blog" }]} />
      <section className="mx-auto max-w-7xl px-6 py-14 lg:px-8">
        <div className="mb-10 flex flex-wrap gap-3" data-testid="blog-category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 font-display text-sm font-bold transition-colors duration-150 ${
                cat === c ? "bg-[#0F172A] text-white" : "border border-slate-200 text-slate-600 hover:border-[#4687FF] hover:text-[#4687FF]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {allPosts === null ? (
          <p className="py-20 text-center text-sm text-slate-500" data-testid="blog-loading">Loading articles…</p>
        ) : posts.length === 0 ? (
          <p className="py-20 text-center text-sm text-slate-500" data-testid="blog-empty-state">
            No articles in this category yet — check back soon.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col border border-slate-200 bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4687FF]">{p.category}</span>
                  <h2 className="mt-3 font-display text-lg font-extrabold leading-snug tracking-tight text-[#0F172A] group-hover:text-[#4687FF]">
                    {p.title}
                  </h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{p.excerpt}</p>
                  <span className="mt-5 text-xs font-medium text-slate-400">{p.date} · {p.reading_time}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
      <CtaBanner />
    </Layout>
  );
};

export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setError(true));
    api.get("/posts").then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (error)
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-6 py-32 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">Not found</p>
          <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-[#0F172A]">This article doesn't exist.</h1>
          <Link to="/blog" className="mt-8 inline-block bg-[#4687FF] px-7 py-3.5 font-display text-sm font-bold text-white hover:bg-[#2563eb]">
            Back to the blog
          </Link>
        </div>
      </Layout>
    );

  if (!post)
    return (
      <Layout>
        <p className="py-40 text-center text-sm text-slate-500" data-testid="blog-post-loading">Loading article…</p>
      </Layout>
    );

  return (
    <Layout>
      <Seo
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        type="article"
        schema={articleSchema({ title: post.title, metaDescription: post.meta_description, date: post.date, author: post.author })}
      />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-6 py-20 lg:px-8">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">
            {post.category} · {post.date} · {post.reading_time}
          </p>
          <h1 className="font-display text-3xl font-black tracking-tighter text-white sm:text-5xl">
            <MaskedLines lines={[post.title]} />
          </h1>
          <p className="mt-6 text-sm font-semibold text-slate-400">By {post.author}</p>
        </div>
      </section>
      <Breadcrumbs crumbs={[{ label: "Blog", to: "/blog" }, { label: post.title }]} />
      <article className="mx-auto max-w-3xl px-6 py-16 lg:px-8" data-testid="blog-post-body">
        <Markdown content={post.content} />
        {related.length > 0 && (
          <Reveal className="mt-8 border-t border-slate-200 pt-10">
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-400">Related Reading</h3>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group border border-slate-200 p-5 transition-colors duration-150 hover:border-[#4687FF]">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4687FF]">{p.category}</span>
                  <p className="mt-2 font-display text-sm font-bold leading-snug text-[#0F172A] group-hover:text-[#4687FF]">{p.title}</p>
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </article>
      <CtaBanner />
    </Layout>
  );
};
